import React, { useState } from "react";
import { Link } from "react-router-dom";
import { BsArrowsFullscreen, BsPeople } from "react-icons/bs";
import { FaTag } from "react-icons/fa";
import { formatPrice } from "../../utils/formatPrice";
import RoomImageCarousel from "./RoomImageCarousel";

const Room = ({ room }) => {
  const [imageLoaded, setImageLoaded] = useState(false);

  const {
    id,
    name,
    imageCard,
    imageRoom,
    size,
    maxPerson,
    description,
    price,
    totalPrice,
    fullPrice,
    warningMessage,
    seasonDiscount,
    firstReservationDiscount,
  } = room;

  const hasCalendarDiscount = totalPrice > 0 && fullPrice > totalPrice;
  const hasDiscount = hasCalendarDiscount || firstReservationDiscount > 0;
  const cleanDescription = description
    ? description.replace(/<[^>]*>/g, "").slice(0, 90)
    : "";

  return (
    <Link
      to={`/room/${id}`}
      className="group block overflow-hidden rounded-sm bg-white shadow-md hover:shadow-xl transition-all duration-500"
    >
      {/* Image section */}
      <div className="relative w-full h-[300px] overflow-hidden">
        <RoomImageCarousel
          images={imageRoom && imageRoom.length ? imageRoom : [imageCard]}
          alt={`Habitación ${name} - Estancia La Alameda`}
          onFirstLoad={() => setImageLoaded(true)}
        />

        {/* Gradient overlay at bottom */}
        <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-black/70 to-transparent pointer-events-none" />

        {/* Discount badges */}
        {imageLoaded && hasDiscount && (
          <div className="absolute top-4 left-4 right-4 flex justify-between items-start pointer-events-none">
            {firstReservationDiscount > 0 ? (
              <span className="bg-accent text-white text-[11px] font-bold px-3 py-1.5 rounded-full flex items-center gap-1.5 shadow-lg font-tertiary uppercase tracking-wider">
                <FaTag className="text-[10px]" />
                {firstReservationDiscount}% 1ra Reserva
              </span>
            ) : (
              <span />
            )}
            {hasCalendarDiscount && seasonDiscount > 0 ? (
              <span className="bg-red-600 text-white text-[11px] font-bold px-3 py-1.5 rounded-full flex items-center gap-1.5 shadow-lg font-tertiary uppercase tracking-wider">
                <FaTag className="text-[10px]" />
                {seasonDiscount}% OFF
              </span>
            ) : (
              <span />
            )}
          </div>
        )}

        {/* Room name over image */}
        {imageLoaded && (
          <div className="absolute bottom-0 left-0 right-0 p-5 pointer-events-none">
            <h3 className="font-primary text-white text-2xl tracking-wide">
              {name}
            </h3>
          </div>
        )}
      </div>

      {/* Content section */}
      <div className="p-5">
        {/* Meta info */}
        <div className="flex items-center gap-4 mb-3">
          <div className="flex items-center gap-2 text-accent">
            <BsArrowsFullscreen className="text-sm" />
            <span className="text-sm font-tertiary uppercase tracking-wider text-primary/80 font-medium">
              {size}m²
            </span>
          </div>
          <div className="w-px h-4 bg-primary/20" />
          <div className="flex items-center gap-2 text-accent">
            <BsPeople className="text-base" />
            <span className="text-sm font-tertiary uppercase tracking-wider text-primary/80 font-medium">
              {Array.isArray(maxPerson) ? maxPerson.join("/") : maxPerson}{" "}
              personas
            </span>
          </div>
        </div>

        {/* Description */}
        <p className="text-sm text-primary/50 leading-relaxed mb-5 line-clamp-2">
          {cleanDescription}...
        </p>

        {/* Price & CTA */}
        {warningMessage ? (
          <div className="bg-secondary text-accent text-center py-3 px-4 rounded font-tertiary uppercase tracking-wider text-sm font-semibold">
            {warningMessage}
          </div>
        ) : (
          <div className="flex items-end justify-between">
            <div>
              {totalPrice && fullPrice > totalPrice && hasDiscount && (
                <span className="text-primary/40 line-through text-sm block">
                  ${formatPrice(fullPrice)}
                </span>
              )}
              <span className="text-2xl font-bold text-primary font-primary">
                {totalPrice
                  ? `$${formatPrice(totalPrice)}`
                  : `$${formatPrice(price)}`}
              </span>
              {!totalPrice && (
                <span className="text-xs text-primary/40 ml-1">/ por persona</span>
              )}
            </div>
            <span className="text-accent font-tertiary uppercase tracking-[2px] text-xs font-semibold group-hover:tracking-[4px] transition-all duration-300">
              Reservar →
            </span>
          </div>
        )}
      </div>
    </Link>
  );
};

export default Room;
